import { Link } from "@tanstack/react-router";
import { Coins, Receipt } from "lucide-react";
import { useState } from "react";

import { CHAT_MESSAGE_COST, TEMPLATE_SEND_COST } from "@mandoob/shared";

import { ResponsiveDialog } from "@/components/shared/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/language";
import { isNative } from "@/lib/capacitor";
import { formatDateMedium, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useSession } from "@/session";

/**
 * The message-credit wallet: balance, top-up and payment history. Shared by the
 * home screen and settings so the balance reads the same wherever it appears.
 * Reads the session itself — the balance is refreshed globally after any send
 * or purchase, so callers pass nothing.
 *
 * Store policy: the charge button is hidden in the native shells (purchases are
 * web-only), leaving the history link on its own.
 */
export function WalletCard({ className }: { className?: string }) {
  const { t, language } = useLanguage();
  const session = useSession();
  const expiresAt = session?.driver.creditsExpiresAt ?? null;
  const expired = !!expiresAt && new Date(expiresAt).getTime() < Date.now();

  // Native app with DISABLE_APP_PAYMENTS: no purchase button — a note points
  // at the website instead. The web client ignores the flag.
  const paymentsHidden = !!session?.appPaymentsDisabled && isNative();

  // "How to use your credit?" — the pricing rules in one small popup, so the
  // chat itself stays free of cost notices.
  const [howToOpen, setHowToOpen] = useState(false);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium tw:text-muted-foreground">
          <Coins className="tw:size-4" strokeWidth={1.75} />
          {t("Mandoob:Home.CreditBalance")}
        </CardTitle>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-1">
          <div className="tw:flex tw:items-baseline tw:gap-2">
            <span className="tw:text-4xl tw:font-bold tw:tabular-nums tw:text-foreground">
              {formatNumber(session?.creditsBalance ?? 0, language)}
            </span>
            <span className="tw:text-sm tw:text-muted-foreground">
              {t("Mandoob:Home.CreditsUnit")}
            </span>
          </div>
          {/* Expired reads even at 0 — that is where the balance went. */}
          {expiresAt && (expired || (session?.creditsBalance ?? 0) > 0) && (
            <span
              className={cn(
                "tw:text-xs",
                expired ? "tw:text-destructive" : "tw:text-muted-foreground",
              )}
            >
              {expired
                ? t("Mandoob:Home.CreditsExpired")
                : t(
                    "Mandoob:Home.CreditsValidUntil",
                    formatDateMedium(expiresAt, language),
                  )}
            </span>
          )}
        </div>
        {paymentsHidden && (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            {t("Mandoob:Home.ManageOnWeb")}
          </p>
        )}
        <div className="tw:flex tw:items-center tw:gap-3">
          {!paymentsHidden && (
            <Button asChild size="sm">
              <Link to="/plans">{t("Mandoob:Home.BuyCredits")}</Link>
            </Button>
          )}
          <Link
            to="/settings/payments"
            className="tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:font-medium tw:text-brand tw:no-underline tw:hover:underline"
          >
            <Receipt className="tw:size-4" strokeWidth={1.75} />
            {t("Mandoob:Home.PaymentHistory")}
          </Link>
        </div>
        <button
          type="button"
          onClick={() => setHowToOpen(true)}
          className="tw:m-0 tw:w-fit tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-xs tw:text-muted-foreground tw:underline tw:underline-offset-2 tw:hover:text-foreground"
        >
          {t("Mandoob:Home.HowToUseCredits")}
        </button>
      </CardContent>

      <ResponsiveDialog
        open={howToOpen}
        onClose={() => setHowToOpen(false)}
        title={t("Mandoob:Home.HowToUseCredits")}
        compact
      >
        <ul className="tw:m-0 tw:flex tw:list-disc tw:flex-col tw:gap-2.5 tw:ps-5 tw:text-sm tw:leading-relaxed tw:text-foreground/85">
          <li>{t("Mandoob:Home.HowToUseTemplate", TEMPLATE_SEND_COST)}</li>
          <li>{t("Mandoob:Home.HowToUseChat", CHAT_MESSAGE_COST)}</li>
          <li>{t("Mandoob:Home.HowToUseReplies")}</li>
          <li>{t("Mandoob:Home.HowToUseValidity")}</li>
        </ul>
      </ResponsiveDialog>
    </Card>
  );
}
